import React from 'react';
import PropTypes from 'prop-types';
import { merge } from 'lodash';
import { Provider } from 'react-redux';

import config from 'login/config/index.js';
import {
    ConfigsService as GeneralConfigsService,
    ContentZonesService,
    LocaleService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import { AccountService, ReduxClient, Translations as GeneralTranslations } from '@sm360/widget-commons-general';
import { PurchaseMethods, Vehicle, ModelYear } from '@sm360/widget-commons-vehicle';
import { Translations as FormTranslations } from '@sm360/widget-commons-forms';

import quotesReducer from 'login/reducers/quotesReducer';

import CONSTANT from 'login/constants/constant.js';
import URL_PARAMS from 'login/constants/urlParams.js';

import LocalConfigsService from 'login/services/ConfigsService/ConfigsService.js';
import TokenService from 'login/services/TokenService/TokenService.js';

import LoginBanner from 'login/components/LoginBanner/LoginBanner.jsx';

import en from 'login/i18n/en.json';
import fr from 'login/i18n/fr.json';

import 'login/components/LoginBannerContainer/LoginBannerContainer.scss';

class LoginBannerContainer extends React.Component {
    constructor(props) {
        super(props);

        merge(
            en,
            Vehicle.Translation.en,
            PurchaseMethods.Translations.en,
            ModelYear.Translations.en,
            FormTranslations.en,
            GeneralTranslations.en
        );
        merge(
            fr,
            Vehicle.Translation.fr,
            PurchaseMethods.Translations.fr,
            ModelYear.Translations.fr,
            FormTranslations.fr,
            GeneralTranslations.fr
        );

        this.state = {
            messageBannerProps: {
                level: '',
                text: '',
            },
        };

        GeneralConfigsService.setOrganizationId(props.organizationId);
        GeneralConfigsService.setOrganizationUnitId(props.organizationUnitId);
        GeneralConfigsService.setOrganizationUnitLocation(props.organizationUnitLocation);
        GeneralConfigsService.setWebsiteId(props.websiteId);
        GeneralConfigsService.setLocation(props.location);
        GeneralConfigsService.setIsShowroomV25Enabled(props.isShowroomV25Enabled);

        LocalConfigsService.setFacebookAppId(this.props.facebookAppId);

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        ContentZonesService.setImageServerBaseUrl(config.source.image);
        ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();

        ReduxClient.addReducers({
            quotes: quotesReducer,
        });
    }

    componentDidMount() {
        TokenService.initialTokenValidation();
        TokenService.initRefreshTokenJob();

        if (this.props.facebookAppId) {
            this.initFacebook();
        }

        this.initMessageBanner();
    }

    configFacebook() {
        const fbApi = WindowProxy.getFBApi();

        if (fbApi) {
            fbApi.init({
                appId: LocalConfigsService.getFacebookAppId(),
                channelUrl: 'channel.html',
                status: true,
                cookie: true,
                xfbml: true,
                version: 'v2.7',
                oauth: true,
            });
        }
    }

    initFacebook() {
        if (typeof WindowProxy.getFBApi() !== 'undefined') {
            this.configFacebook();
        } else if (typeof WindowProxy.get() !== 'undefined') {
            WindowProxy.get().fbAsyncInit = () => this.configFacebook();
            const $window = WindowProxy.get();

            if ($window) {
                const id = 'facebook-jssdk';
                const ref = $window.document.getElementsByTagName('script')[0];

                if ($window.document.getElementById(id)) {
                    return;
                }

                const js = $window.document.createElement('script');
                js.id = id;
                js.async = true;
                js.src = 'https://connect.facebook.net/en_US/sdk.js';

                ref.parentNode.insertBefore(js, ref);
            }
        }
    }

    initMessageBanner() {
        const validationKey = UrlService.getUrlParameter(URL_PARAMS.VALIDATION_KEY);
        const username = UrlService.getUrlParameter(URL_PARAMS.USERNAME);
        const id = UrlService.getUrlParameter(URL_PARAMS.ID);

        if (validationKey && (username || id)) {
            AccountService.confirmEmail(username, validationKey, parseInt(id, 10))
                .then(() => {
                    this.setState({
                        messageBannerProps: {
                            level: CONSTANT.MESSAGE.LEVELS.SUCCESS,
                            text: LocaleService.t('LOGIN.SUCCESS_MESSAGE.EMAIL_CONFIRMED'),
                        },
                    });
                })
                .catch(() => {
                    this.setState({
                        messageBannerProps: {
                            level: CONSTANT.MESSAGE.LEVELS.ERROR,
                            text: LocaleService.t('LOGIN.ERROR_MESSAGE.EMAIL_VALIDATION_CODE_NOT_FOUND'),
                        },
                    });
                });
        }
    }

    render() {
        const block = <LoginBanner {...this.props} messageBanner={this.state.messageBannerProps} />;

        return (
            <Provider store={this.storeRedux}>
                <div id="loginWidget" className="widget-login">
                    {block}
                </div>
            </Provider>
        );
    }
}

LoginBannerContainer.defaultProps = {
    facebookAppId: '',
};

LoginBannerContainer.propTypes = {
    facebookAppId: PropTypes.string,
    websiteId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    isShowroomV25Enabled: PropTypes.bool.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
};

LoginBannerContainer.displayName = 'LoginBannerContainer';

export default LoginBannerContainer;
